import 'swiper/css'
import './index.css'
import cn from 'clsx'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation } from 'swiper/modules'
import type { GridSizes } from './index'
type Props = {
  children: React.ReactNode
  singleRow?: boolean
  size: GridSizes
  id?: string | number
  onRenderSlider?: (...args: Array<any>) => any
  slideContainerComponent?: (...args: Array<any>) => any
}
const MIN_GRID_HEIGHT = 24

const getSildes = ({ visibleGrids, gridItems }) => {
  const slides = []

  for (
    let index = 0;
    index < Math.ceil(gridItems.length / visibleGrids);
    index++
  ) {
    slides.push(
      gridItems.slice(index * visibleGrids, (index + 1) * visibleGrids),
    )
  }

  return slides
}

const GridWithSlider = (
  props: Props,
): null | React.ReactNode | React.ReactElement<'div'> => {
  const gridContainerRef = useRef(null)
  const [visibleGrids, setVisibleGrids] = useState(0)

  const checkVisibleGrids = useCallback(() => {
    if (gridContainerRef.current?.children && props.onRenderSlider) {
      const children = [...gridContainerRef.current.children]
      let newVisibleGrids = 0
      children.forEach(item => {
        if (item.offsetHeight > MIN_GRID_HEIGHT) {
          newVisibleGrids++
        }
      })

      if (newVisibleGrids !== visibleGrids) {
        setVisibleGrids(newVisibleGrids)
      }
    }
  }, [visibleGrids, props.onRenderSlider])

  useEffect(() => {
    window.addEventListener('resize', checkVisibleGrids)
    return () => {
      window.removeEventListener('resize', checkVisibleGrids)
    }
  }, [])
  useEffect(() => {
    checkVisibleGrids()
  }, [gridContainerRef, checkVisibleGrids])
  const renderSlider =
    visibleGrids > 0 &&
    visibleGrids < React.Children.count(props.children) &&
    props.onRenderSlider
  const gridItems = React.Children.map(
    props.children,
    (child: React.ReactNode, index: number) =>
      child && (
        <div className="Grid-item" key={index}>
          {child}
        </div>
      ),
  )
  const gridContainer = (
    <div
      ref={gridContainerRef}
      className={cn('Grid', {
        'Grid--small': props.size === 'small',
        'Grid--medium': props.size === 'medium',
        'Grid--large': props.size === 'large',
        'Grid--mlarge': props.size === 'mlarge',
        'Grid--xlarge': props.size === 'xlarge',
        'Grid--xxlarge': props.size === 'xxlarge',
        'Grid--single-row': props.singleRow,
        'Grid--hide': renderSlider,
      })}
    >
      {gridItems}
    </div>
  )

  if (renderSlider) {
    const slides = getSildes({
      gridItems,
      visibleGrids,
    })
    if (props.onRenderSlider && !props.onRenderSlider(true)) return null
    return (
      <>
        <Swiper
          modules={[Navigation]}
          spaceBetween={24}
          slidesPerView={1}
          loop={false}
          navigation={{
            nextEl: `#btn-next-${props.id || ''}`,
            prevEl: `#btn-prev-${props.id || ''}`,
          }}
          threshold={2}
        >
          {slides.map((slide, index) => {
            const Container = props.slideContainerComponent || React.Fragment
            return (
              <SwiperSlide key={index}>
                <Container>
                  <div
                    className={cn('Grid', {
                      'Grid--small': props.size === 'small',
                      'Grid--medium': props.size === 'medium',
                      'Grid--large': props.size === 'large',
                      'Grid--mlarge': props.size === 'mlarge',
                      'Grid--xlarge': props.size === 'xlarge',
                      'Grid--xxlarge': props.size === 'xxlarge',
                      'Grid--single-row': props.singleRow,
                    })}
                  >
                    {slide}
                  </div>
                </Container>
              </SwiperSlide>
            )
          })}
        </Swiper>
        {gridContainer}
      </>
    )
  }

  if (props.onRenderSlider) {
    props.onRenderSlider(false)
  }

  return gridContainer
}

export default GridWithSlider
